import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import PageTransition from '../components/Motion/PageTransition';

/**
 * Integration Tools Page - Detailed overview of Data Advisory Services tools
 * Showcases all integration technologies and platforms
 */
function IntegrationTools() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const headerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  // Systems we can integrate with
  const integrationSystems = [
    {
      id: 'jira',
      name: 'Atlassian Jira',
      category: 'Project Management',
      description: 'Integrate with Atlassian Jira to track projects, issues, and workflows. Access comprehensive project data, sprint metrics, and team performance analytics for data-driven project management.',
      features: [
        'Complete Jira API integration',
        'Real-time issue and sprint tracking',
        'Custom field and workflow support',
        'Agile metrics and burndown charts',
        'Time tracking and worklog analysis',
        'Cross-project reporting and analytics'
      ],
      benefits: 'Transform Jira data into actionable insights. Track project health, team velocity, and delivery metrics in real-time.',
      useCases: 'Sprint Analytics, Team Performance, Project Tracking, Resource Planning'
    },
    {
      id: 'bullhorn',
      name: 'Bullhorn',
      category: 'ATS & CRM',
      description: 'Native integration with Bullhorn ATS and CRM platform for recruitment and staffing analytics. Connect candidate pipelines, placements, and sales data for comprehensive workforce intelligence.',
      features: [
        'Full Bullhorn REST API integration',
        'Candidate and job data synchronization',
        'Placement and revenue tracking',
        'Sales pipeline and activity metrics',
        'Custom fields and entity support',
        'Real-time data refresh capabilities'
      ],
      benefits: 'Unlock recruitment intelligence from your Bullhorn data. Optimize placement rates and improve candidate-to-hire ratios.',
      useCases: 'Recruitment Analytics, Sales Dashboards, Placement Tracking, Candidate Pipeline Analysis',
      link: 'https://www.bullhorn.com/nl/'
    },
    {
      id: 'servicenow',
      name: 'ServiceNow',
      category: 'ITSM Platform',
      description: 'Deep integration with ServiceNow for IT service management and operations analytics. Extract incident, problem, change, and asset data for comprehensive ITSM reporting and insights.',
      features: [
        'ServiceNow REST and Table API integration',
        'Incident and problem management data',
        'Change and release tracking',
        'Service catalog and CMDB integration',
        'Custom application support',
        'Real-time operational metrics'
      ],
      benefits: 'Drive IT operational excellence with ServiceNow analytics. Reduce mean time to resolution and improve service quality.',
      useCases: 'ITSM Dashboards, Incident Analytics, Change Management Reports, Asset Tracking'
    },
    {
      id: 'exact-online',
      name: 'Exact Online',
      category: 'ERP System',
      description: 'Native integration with Exact Online ERP system for comprehensive financial and operational data access. Full API coverage for all modules including financials, inventory, CRM, and project management.',
      features: [
        'Complete API coverage for all Exact modules',
        'Real-time data synchronization',
        'OAuth authentication and security',
        'Division and multi-company support',
        'Historical data extraction and incremental loads',
        'Custom field and extension support'
      ],
      benefits: 'Unlock the full potential of your Exact Online data. Gain real-time visibility into financial and operational metrics.',
      useCases: 'Financial Reporting, Inventory Analytics, Sales Dashboards, Project Tracking'
    },
    {
      id: 'dynamics-365',
      name: 'Microsoft Dynamics 365',
      category: 'ERP System',
      description: 'Deep integration with Microsoft Dynamics 365 Business Central for end-to-end business intelligence. Support for all modules including financials, supply chain, manufacturing, and warehouse management.',
      features: [
        'Native Dataverse and OData integration',
        'Support for Business Central and NAV',
        'Custom business logic and workflows',
        'Multi-environment support (sandbox, production)',
        'Extension and customization integration',
        'Real-time and batch data extraction'
      ],
      benefits: 'Comprehensive integration with your Dynamics ecosystem. Enable advanced analytics across all business operations.',
      useCases: 'ERP Reporting, Supply Chain Analytics, Manufacturing KPIs, WMS Dashboards'
    },
    {
      id: 'sap-s4hana',
      name: 'SAP S/4HANA',
      icon: SAPS4HANAIcon,
      category: 'ERP System',
      description: 'Enterprise integration with SAP S/4HANA for comprehensive business intelligence and analytics. Connect to SAP\'s intelligent ERP suite to extract financial, logistics, procurement, and operational data for advanced reporting and insights.',
      features: [
        'OData and REST API integration',
        'Support for SAP HANA database connections',
        'Real-time and batch data extraction',
        'Multi-module support (FI, CO, MM, SD, PP)',
        'Custom CDS views and table access',
        'Integration with SAP BW and SAP Analytics Cloud'
      ],
      benefits: 'Unlock enterprise-wide insights from your SAP landscape. Enable real-time analytics and reporting across all SAP modules.',
      useCases: 'Financial Consolidation, Supply Chain Visibility, Procurement Analytics, Production Planning'
    }
  ];

  // Stack connectors and tools we use
  const stackConnectors = [
    {
      id: 'fabric-notebooks',
      name: 'Fabric Notebooks',
      category: 'Data Engineering',
      description: 'Leverage Microsoft Fabric Notebooks for collaborative data engineering and advanced analytics. Build scalable data pipelines using Python, Spark, and SQL with real-time collaboration capabilities.',
      features: [
        'Interactive Python, Spark, and SQL development',
        'Real-time collaboration with version control',
        'Seamless integration with Fabric data sources',
        'Built-in visualization and reporting',
        'Scheduled execution and automation',
        'Enterprise-grade security and governance'
      ],
      benefits: 'Accelerate data engineering workflows with a unified notebook experience. Reduce development time by 50% with built-in data lake access.',
      useCases: 'Data Transformation, Advanced Analytics, Machine Learning Pipelines, Data Quality Checks'
    },
    {
      id: 'fabric-flows',
      name: 'Fabric Data Flows',
      category: 'Data Orchestration',
      description: 'Orchestrate complex data workflows with Microsoft Fabric Data Flows. Create end-to-end data integration pipelines with visual designers and code-based approaches for maximum flexibility.',
      features: [
        'Visual pipeline designer for no-code development',
        'Schedule and trigger-based execution',
        'Built-in error handling and monitoring',
        'Parallel processing for high performance',
        'Integration with 100+ data sources',
        'Advanced transformation capabilities'
      ],
      benefits: 'Build enterprise-grade data pipelines without heavy coding. Automate complex workflows and reduce manual intervention by 90%.',
      useCases: 'ETL Pipelines, Data Migration, Batch Processing, Real-time Data Sync'
    },
    {
      id: 'dataflow',
      name: 'Power BI Dataflows',
      category: 'Data Preparation',
      description: 'Self-service data preparation at scale with Power BI Dataflows. Enable business users to prepare, transform, and enrich data without IT bottlenecks while maintaining governance and security.',
      features: [
        'Self-service data preparation for business users',
        'Reusable transformation logic across datasets',
        'Incremental refresh for large datasets',
        'Power Query M language support',
        'Computed entities for complex calculations',
        'Central data repository with security'
      ],
      benefits: 'Empower business users to prepare their own data while maintaining IT governance. Reduce data preparation time by 70%.',
      useCases: 'Data Cleansing, Master Data Management, Data Enrichment, Reference Tables'
    },
    {
      id: 'power-query',
      name: 'Power Query',
      category: 'Data Transformation',
      description: 'Industry-leading data transformation engine for cleaning, shaping, and combining data from multiple sources. The universal data connectivity and transformation layer across Microsoft analytics products.',
      features: [
        'Connect to 100+ data sources natively',
        'Intuitive UI for data transformations',
        'Advanced M language for custom logic',
        'Query folding for optimal performance',
        'Parameter-driven dynamic queries',
        'Reusable custom functions'
      ],
      benefits: 'Transform data with unmatched flexibility and performance. Reduce manual data wrangling by 85% with automated refresh.',
      useCases: 'Data Cleaning, Data Shaping, Data Combining, API Integration'
    },
    {
      id: 'sql-stored-procedures',
      name: 'SQL Stored Procedures',
      category: 'Database Integration',
      description: 'Robust database integration using optimized SQL stored procedures for transactional systems and data warehouses. Ensure data integrity, security, and high-performance data operations.',
      features: [
        'Optimized T-SQL for SQL Server and Azure SQL',
        'Transaction management and ACID compliance',
        'Complex business logic implementation',
        'Batch processing and bulk operations',
        'Error handling and logging',
        'Performance tuning and indexing strategies'
      ],
      benefits: 'Maximize database performance with server-side processing. Reduce network overhead by 60% and improve query speed by 80%.',
      useCases: 'Data Warehousing, Transaction Processing, Data Migration, Report Generation'
    },
    {
      id: 'custom-connectors',
      name: 'Custom Connectors',
      category: 'API Integration',
      description: 'Develop custom connectors for proprietary systems, legacy applications, and specialized data sources. Extend Power Platform and Azure capabilities to integrate with any system.',
      features: [
        'RESTful API and SOAP web service integration',
        'OAuth 2.0 and custom authentication',
        'Certified connectors for Power Platform',
        'Rate limiting and error retry logic',
        'Data pagination and incremental loading',
        'Custom action and trigger definitions'
      ],
      benefits: 'Connect to any data source regardless of complexity. Enable self-service analytics for previously inaccessible systems.',
      useCases: 'Proprietary Systems, Legacy Applications, Custom APIs, Third-party Services'
    },
    {
      id: 'n8n',
      name: 'n8n Workflow Automation',
      category: 'Workflow Automation',
      description: 'Fair-code workflow automation platform for building complex integration workflows with visual programming. Connect apps, APIs, and databases with 400+ pre-built nodes for rapid integration development and deployment.',
      features: [
        '400+ pre-built integration nodes',
        'Visual workflow designer with drag-and-drop',
        'Self-hosted for complete data control',
        'Custom JavaScript code execution in workflows',
        'Webhook triggers and scheduled executions',
        'Error handling and workflow branching logic'
      ],
      benefits: 'Build complex integration workflows 5x faster with visual programming. Maintain full control over your data with self-hosted deployment.',
      useCases: 'API Orchestration, Data Synchronization, Event-driven Automation, Multi-step Workflows'
    }
  ];

  return (
    <PageTransition>
      <div className="bg-gradient-to-b from-gray-50 to-white py-16">
        <div className="container mx-auto px-4">
          {/* Header Section */}
          <motion.div
            className="max-w-4xl mx-auto text-center mb-16"
            initial="hidden"
            animate="visible"
            variants={headerVariants}
          >
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Integration Tools & Technologies
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              We leverage cutting-edge integration tools to connect your entire data ecosystem.
              From modern cloud platforms to legacy systems, we make your data flow seamlessly.
            </p>
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-green-50 rounded-full text-green-700 font-semibold">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Connect to 500+ Data Sources
            </div>
          </motion.div>

          {/* Systems We Can Integrate Section */}
          <motion.div
            className="max-w-7xl mx-auto mb-20"
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Systems We Can Integrate</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                We seamlessly connect your business-critical systems to unlock powerful insights and streamline operations.
              </p>
            </div>

            <div className="space-y-8">
              {integrationSystems.map((system) => (
                <motion.div
                  key={system.id}
                  className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden"
                  variants={cardVariants}
                  whileHover={{ y: -4 }}
                >
                  <div className="grid md:grid-cols-3 gap-6 p-8">
                    {/* Category and Name */}
                    <div className="md:col-span-1 flex flex-col text-center md:text-left">
                      <div className="inline-flex items-center justify-center md:justify-start mb-3">
                        <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-bold uppercase tracking-wider rounded-full">
                          {system.category}
                        </span>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        {system.link ? (
                          <a href={system.link} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 transition-colors">
                            {system.name}
                          </a>
                        ) : (
                          system.name
                        )}
                      </h3>
                    </div>

                    {/* Description and Details */}
                    <div className="md:col-span-2">
                      <p className="text-gray-600 mb-6 text-lg leading-relaxed">
                        {system.description}
                      </p>

                      {/* Features Grid */}
                      <div className="mb-6">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                          Key Capabilities
                        </h4>
                        <div className="grid sm:grid-cols-2 gap-3">
                          {system.features.map((feature, idx) => (
                            <div key={idx} className="flex items-start gap-2">
                              <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              <span className="text-sm text-gray-700">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Benefits */}
                      <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-l-4 border-green-600 mb-4">
                        <p className="text-sm font-semibold text-gray-800">
                          <span className="text-green-700">Business Impact:</span> {system.benefits}
                        </p>
                      </div>

                      {/* Use Cases */}
                      <div className="flex flex-wrap gap-2">
                        {system.useCases.split(', ').map((useCase, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-full font-medium"
                          >
                            {useCase}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Stack Connectors Section */}
          <motion.div
            className="max-w-7xl mx-auto"
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Stack Connectors We Use</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Our powerful integration stack leverages industry-leading tools and technologies to transform and connect your data.
              </p>
            </div>

            <div className="space-y-8">
              {stackConnectors.map((tool) => (
              <motion.div
                key={tool.id}
                className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden"
                variants={cardVariants}
                whileHover={{ y: -4 }}
              >
                <div className="grid md:grid-cols-3 gap-6 p-8">
                  {/* Category and Name */}
                  <div className="md:col-span-1 flex flex-col text-center md:text-left">
                    <div className="inline-flex items-center justify-center md:justify-start mb-3">
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-bold uppercase tracking-wider rounded-full">
                        {tool.category}
                      </span>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{tool.name}</h3>
                  </div>

                  {/* Description and Details */}
                  <div className="md:col-span-2">
                    <p className="text-gray-600 mb-6 text-lg leading-relaxed">
                      {tool.description}
                    </p>

                    {/* Features Grid */}
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                        Key Capabilities
                      </h4>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {tool.features.map((feature, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-sm text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Benefits */}
                    <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-l-4 border-green-600 mb-4">
                      <p className="text-sm font-semibold text-gray-800">
                        <span className="text-green-700">Business Impact:</span> {tool.benefits}
                      </p>
                    </div>

                    {/* Use Cases */}
                    <div className="flex flex-wrap gap-2">
                      {tool.useCases.split(', ').map((useCase, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-full font-medium"
                        >
                          {useCase}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
            </div>
          </motion.div>

          {/* CTA Section */}
          <motion.div
            className="max-w-4xl mx-auto text-center mt-16 p-8 bg-gradient-to-r from-green-600 to-teal-600 rounded-2xl text-white shadow-2xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold mb-4">Need a Custom Integration?</h2>
            <p className="text-green-100 mb-6 text-lg">
              Every business is unique. Let's discuss how we can integrate your specific systems and data sources.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-block bg-white text-green-600 px-8 py-3 rounded-lg font-semibold hover:bg-green-50 transition-colors shadow-lg"
              >
                Discuss Your Integration Needs
              </Link>
              <Link
                to="/projects"
                className="inline-block bg-green-700 text-white px-8 py-3 rounded-lg font-semibold hover:bg-green-800 transition-colors border-2 border-white"
              >
                View Integration Projects
              </Link>
            </div>
          </motion.div>

          {/* Back to Services */}
          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-flex items-center text-green-600 hover:text-green-800 font-semibold transition-colors group"
            >
              <svg
                className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Services
            </Link>
          </div>
        </div>
      </div>
    </PageTransition>
  );
}

export default IntegrationTools;

